import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { request } from '../../../../Infrastructure'
import { CATEGORY_ID, CMS_BLOCK_IDENTIFIER } from '../../lib/constants'
import { getPromoCartBlock, getPromoCartItems } from '../../queries'

import type { CmsBlockData, GraphQLProduct } from '../../types'

/** GraphQL response shape for the promo CMS block query */
interface PromoCartBlockResponse {
  cmsBlocks: { items: CmsBlockData[] }
}

/** GraphQL response shape for the promo categories query */
interface PromoCartCategoriesResponse {
  categoryList: {
    children?: { products?: { items: GraphQLProduct[] } }[]
  }[]
}

interface PromoDataResult {
  blockData: CmsBlockData | null
  firstChildCategoryItems: GraphQLProduct[]
  secondChildCategoryItems: GraphQLProduct[]
  loading: boolean
  error: Error | null
}

const EMPTY_ITEMS: GraphQLProduct[] = []

/**
 * Hook for fetching promo CMS block and category products via React Query.
 *
 * Extracts data fetching logic from the old PromoStateProvider.
 */
export function usePromoData(): PromoDataResult {
  const cmsBlockQuery = useQuery({
    queryKey: ['promoCartBlock', CMS_BLOCK_IDENTIFIER],
    queryFn: async () =>
      request<PromoCartBlockResponse>(getPromoCartBlock, { identifier: CMS_BLOCK_IDENTIFIER }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const categoriesQuery = useQuery({
    queryKey: ['promoCartItems', CATEGORY_ID],
    queryFn: async () =>
      request<PromoCartCategoriesResponse>(getPromoCartItems, { categoryId: CATEGORY_ID }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const blockData = useMemo<CmsBlockData | null>(
    () => cmsBlockQuery.data?.data?.cmsBlocks?.items[0] ?? null,
    [cmsBlockQuery.data]
  )

  const categoriesData = useMemo(
    () => categoriesQuery.data?.data?.categoryList?.[0] ?? null,
    [categoriesQuery.data]
  )

  const firstChildCategoryItems = useMemo<GraphQLProduct[]>(
    () => categoriesData?.children?.[0]?.products?.items ?? EMPTY_ITEMS,
    [categoriesData]
  )

  const secondChildCategoryItems = useMemo<GraphQLProduct[]>(
    () => categoriesData?.children?.[1]?.products?.items ?? EMPTY_ITEMS,
    [categoriesData]
  )

  const loading = cmsBlockQuery.isLoading || categoriesQuery.isLoading
  const error = (cmsBlockQuery.error ?? categoriesQuery.error)

  return { blockData, firstChildCategoryItems, secondChildCategoryItems, loading, error }
}
